import {
  BatterySavingsDto,
  BatterySavingsModel,
  FirstThreeSixtySavingsEntryDto,
  FirstThreeSixtySavingsEntryModel,
  WallboxSavingsDto,
  WallboxSavingsModel,
} from '../../system-data-handler/types'
import {
  batterySavingsDtoToModel,
  firstSavingDtoToModel,
  wallboxSavingsDtoToModel,
} from 'src/Libs/dynamic-power-prices/api/dto/three-sixty-savings.transformers'
import { ThreeSixtySavingsResolution } from '../types'
import { AuthFetchError, AuthFetchWithResult } from '@e3dc-react/shell/Types'
import { ApiResult } from 'src/Libs/api'
import { handleFetchError } from 'src/Libs/api/utils'

/**
 * Fetches the savings from wallboxes in a single storage.
 */
export const getWallboxSavings = async (
  authFetch: AuthFetchWithResult,
  shouldUseNewRoute: boolean,
  storageId?: string,
  from?: number,
  to?: number,
  resolution?: ThreeSixtySavingsResolution
): Promise<ApiResult<WallboxSavingsModel, AuthFetchError | Error>> => {
  if (!storageId) {
    return new ApiResult<WallboxSavingsModel, AuthFetchError | Error>(null, true, new Error('No storageId provided'))
  }
  const oldPath = `e-mobility/${storageId}/charging/price-time-slot/savings`
  const newPath = `tariffs/${storageId}/savings/e-mobility`
  const pathToUse = shouldUseNewRoute ? newPath : oldPath

  const newEndPoint = process.env.REACT_APP_INSTALLATIONS_ENDPOINT
  const oldEndPoint = process.env.REACT_APP_EMOBILITY_ENDPOINT
  const endpointToUse = shouldUseNewRoute ? newEndPoint : oldEndPoint

  try {
    const response = await authFetch<WallboxSavingsDto[]>({
      path: pathToUse,
      payload: { from, to, resolution },
      endpoint: endpointToUse,
    })

    const data = wallboxSavingsDtoToModel(response)
    return new ApiResult<WallboxSavingsModel, AuthFetchError | Error>(data)
  } catch (error) {
    return handleFetchError<WallboxSavingsModel>(error)
  }
}

/**
 * Fetches the savings from wallboxes in a farm.
 */
export const getWallboxSavingsBulk = async (
  authFetch: AuthFetchWithResult,
  storagesIds: string[],
  shouldUseNewRoute: boolean,
  from?: number,
  to?: number,
  resolution?: ThreeSixtySavingsResolution
): Promise<ApiResult<WallboxSavingsModel, AuthFetchError | Error>> => {
  const oldPath = `e-mobility/bulk/charging/price-time-slot/savings`
  const newPath = `tariffs/bulk/savings/e-mobility`
  const pathToUse = shouldUseNewRoute ? newPath : oldPath
  const newEndPoint = process.env.REACT_APP_INSTALLATIONS_ENDPOINT
  const oldEndPoint = process.env.REACT_APP_EMOBILITY_ENDPOINT
  const endpointToUse = shouldUseNewRoute ? newEndPoint : oldEndPoint

  try {
    const response = await authFetch<WallboxSavingsDto[]>({
      endpoint: endpointToUse,
      path: pathToUse,
      payload: { sn: storagesIds, from, to, resolution },
    })

    const data = wallboxSavingsDtoToModel(response)
    return new ApiResult<WallboxSavingsModel, AuthFetchError | Error>(data)
  } catch (error) {
    return handleFetchError<WallboxSavingsModel>(error)
  }
}

/**
 * Fetches the savings from battery in a single storage.
 */
export const getBatterySavings = async (
  authFetch: AuthFetchWithResult,
  storageId?: string,
  from?: number,
  to?: number,
  resolution?: ThreeSixtySavingsResolution
): Promise<ApiResult<BatterySavingsModel, AuthFetchError | Error>> => {
  if (!storageId) {
    return new ApiResult<BatterySavingsModel, AuthFetchError | Error>(null, true, new Error('No storageId provided'))
  }
  try {
    const response = await authFetch<BatterySavingsDto[]>({
      path: `tariffs/${storageId}/savings/storage`,
      payload: { from, to, resolution },
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    })

    const data = batterySavingsDtoToModel(response)
    return new ApiResult<BatterySavingsModel, AuthFetchError | Error>(data)
  } catch (error) {
    return handleFetchError<BatterySavingsModel>(error)
  }
}

/**
 * Fetches the savings from battery in a farm.
 */
export const getBatterySavingsBulk = async (
  authFetch: AuthFetchWithResult,
  storagesIds: string[],
  from?: number,
  to?: number,
  resolution?: ThreeSixtySavingsResolution
): Promise<ApiResult<BatterySavingsModel, AuthFetchError | Error>> => {
  try {
    const response = await authFetch<BatterySavingsDto[]>({
      path: 'tariffs/bulk/savings/storage',
      payload: { sn: storagesIds, from, to, resolution },
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    })

    const data = batterySavingsDtoToModel(response)
    return new ApiResult<BatterySavingsModel, AuthFetchError | Error>(data)
  } catch (error) {
    return handleFetchError<BatterySavingsModel>(error)
  }
}

/**
 * Fetches the first savings entry.
 */
export const getFirstThreeSixtySavingsEntry = async (
  authFetch: AuthFetchWithResult,
  storageId: string
): Promise<ApiResult<FirstThreeSixtySavingsEntryModel, AuthFetchError | Error>> => {
  if (!storageId) {
    return new ApiResult<FirstThreeSixtySavingsEntryModel, AuthFetchError | Error>(null, true, new Error('No storageId provided'))
  }
  try {
    const response = await authFetch<FirstThreeSixtySavingsEntryDto>({
      path: `tariffs/${storageId}/savings/storage/first`,
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    })

    const data = firstSavingDtoToModel(response)
    return new ApiResult<FirstThreeSixtySavingsEntryModel, AuthFetchError | Error>(data)
  } catch (error) {
    return handleFetchError<FirstThreeSixtySavingsEntryModel>(error)
  }
}
